import axios from 'axios';
import { API_URL } from '../config/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL
});

// Add auth token to requests and handle Content-Type
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // If data is FormData, let axios set the Content-Type automatically (with boundary)
  // Otherwise, set it to application/json
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  
  return config;
});

// Products API
export const productsApi = {
  // Get all products
  getAll: async (params = {}) => {
    const response = await api.get('/products', { params });
    return response.data;
  },

  // Get featured products
  getFeatured: async () => {
    const response = await api.get('/products/featured');
    return response.data;
  },

  // Get popular products
  getPopular: async () => {
    const response = await api.get('/products/popular');
    return response.data;
  },

  // Get latest/new products
  getLatest: async () => {
    const response = await api.get('/products/latest');
    return response.data;
  },

  // Get product by ID
  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  // Get products by user ID (for seller dashboard)
  getByUserId: async (userId, params = {}) => {
    const response = await api.get(`/products/user/${userId}`, { params });
    return response.data;
  },

  // Create new product
  create: async (productData) => {
    const response = await api.post('/products', productData);
    return response.data;
  },

  // Update product
  update: async (id, productData) => {
    const response = await api.put(`/products/${id}`, productData);
    return response.data;
  },

  // Update product status
  updateStatus: async (id, status) => {
    const response = await api.patch(`/products/${id}/status`, { status });
    return response.data;
  },

  // Delete product
  delete: async (id) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  },

  // Bulk delete products
  bulkDelete: async (ids) => {
    const response = await api.post('/products/bulk-delete', { ids });
    return response.data;
  },

  // Bulk update status
  bulkUpdateStatus: async (ids, status) => {
    const response = await api.post('/products/bulk-status', { ids, status });
    return response.data;
  }
};

// Sellers API
export const sellersApi = {
  // Get all sellers
  getAll: async (params = {}) => {
    const response = await api.get('/sellers', { params });
    return response.data;
  },

  // Get top sellers
  getTop: async () => {
    const response = await api.get('/sellers/top');
    return response.data;
  },

  // Get seller by ID
  getById: async (id) => {
    const response = await api.get(`/sellers/${id}`);
    return response.data;
  },

  // Get seller profile by user ID
  getProfileByUserId: async (userId) => {
    const response = await api.get(`/sellers/profile/${userId}`);
    return response.data;
  },

  // Create seller profile
  createProfile: async (profileData) => {
    const response = await api.post('/sellers/profile', profileData);
    return response.data;
  },

  // Update seller profile
  updateProfile: async (id, profileData) => {
    const response = await api.put(`/sellers/profile/${id}`, profileData);
    return response.data;
  },

  // Update profile step (wizard navigation)
  updateProfileStep: async (id, step, stepData) => {
    const response = await api.patch(`/sellers/profile/${id}/step`, { step, stepData });
    return response.data;
  },

  // Update seller status
  updateStatus: async (id, status) => {
    const response = await api.patch(`/sellers/${id}/status`, { status });
    return response.data;
  },

  // Delete seller
  delete: async (id) => {
    const response = await api.delete(`/sellers/${id}`);
    return response.data;
  },

  // Bulk delete sellers
  bulkDelete: async (ids) => {
    const response = await api.post('/sellers/bulk-delete', { ids });
    return response.data;
  },

  // Bulk update status
  bulkUpdateStatus: async (ids, status) => {
    const response = await api.post('/sellers/bulk-status', { ids, status });
    return response.data;
  }
};

// Brands API
export const brandsApi = {
  // Get all brands
  getAll: async (params = {}) => {
    const response = await api.get('/brands', { params });
    return response.data;
  },

  // Get highlighted brands (only from sellers with canHighlightBrand permission)
  getHighlighted: async () => {
    const response = await api.get('/brands/highlighted');
    return response.data;
  },

  // Get brand by ID
  getById: async (id) => {
    const response = await api.get(`/brands/${id}`);
    return response.data;
  },

  // Create new brand
  create: async (formData) => {
    // Don't set Content-Type header - axios will set it automatically with correct boundary for FormData
    const response = await api.post('/brands', formData);
    return response.data;
  },

  // Update brand
  update: async (id, formData) => {
    // Don't set Content-Type header - axios will set it automatically with correct boundary for FormData
    const response = await api.put(`/brands/${id}`, formData);
    return response.data;
  },

  // Delete brand
  delete: async (id) => {
    const response = await api.delete(`/brands/${id}`);
    return response.data;
  }
};

// Categories API
export const categoriesApi = {
  // Get all categories
  getAll: async () => {
    const response = await api.get('/categories');
    return response.data;
  },

  // Get category by ID
  getById: async (id) => {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  }
};

// RFQ (Request for Quotation) API
export const rfqApi = {
  // Get all RFQs
  getAll: async (params = {}) => {
    const response = await api.get('/rfq', { params });
    return response.data;
  },

  // Get RFQ statistics
  getStats: async (sellerId = null) => {
    const params = sellerId ? { sellerId } : {};
    const response = await api.get('/rfq/stats', { params });
    return response.data;
  },

  // Get RFQs for a specific seller
  getBySellerId: async (sellerId, params = {}) => {
    const response = await api.get(`/rfq/seller/${sellerId}`, { params });
    return response.data;
  },

  // Get RFQs submitted by a user
  getByUserId: async (userId, params = {}) => {
    const response = await api.get(`/rfq/user/${userId}`, { params });
    return response.data;
  },

  // Get single RFQ by ID
  getById: async (id) => {
    const response = await api.get(`/rfq/${id}`);
    return response.data;
  },

  // Create new RFQ
  create: async (rfqData) => {
    const response = await api.post('/rfq', rfqData);
    return response.data;
  },

  // Update RFQ
  update: async (id, rfqData) => {
    const response = await api.put(`/rfq/${id}`, rfqData);
    return response.data;
  },

  // Update RFQ status
  updateStatus: async (id, status) => {
    const response = await api.patch(`/rfq/${id}/status`, { status });
    return response.data;
  },

  // Delete RFQ
  delete: async (id) => {
    const response = await api.delete(`/rfq/${id}`);
    return response.data;
  },

  // Bulk delete RFQs
  bulkDelete: async (ids) => {
    const response = await api.post('/rfq/bulk-delete', { ids });
    return response.data;
  },

  // Bulk update status
  bulkUpdateStatus: async (ids, status) => {
    const response = await api.post('/rfq/bulk-status', { ids, status });
    return response.data;
  },

  // Reply to RFQ - creates conversation and sends message
  // Note: Only Global Enterprise plan can reply to RFQs
  reply: async (rfqId, sellerId, message) => {
    try {
      const response = await api.post(`/rfq/${rfqId}/reply`, { sellerId, message });
      return response.data;
    } catch (error) {
      // Check if it's a messaging permission error
      if (error.response?.status === 403 && error.response?.data?.errorCode === 'MESSAGING_NOT_ALLOWED') {
        const customError = new Error(error.response.data.message || 'Messaging is only available with Global Enterprise plan');
        customError.code = 'MESSAGING_NOT_ALLOWED';
        customError.requiredFeature = 'canLiveChat';
        throw customError;
      }
      throw error;
    }
  }
};

// Upload API
export const uploadApi = {
  // Upload single image
  uploadImage: async (file, type = 'images') => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post(`/upload/image?type=${type}`, formData);
    return response.data;
  },

  // Upload multiple images
  uploadImages: async (files, type = 'images') => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });
    const response = await api.post(`/upload/images?type=${type}`, formData);
    return response.data;
  },

  // Upload any file (documents, PDFs, etc.)
  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/upload/file', formData);
    return response.data;
  }
};

// Favorites API
export const favoritesApi = {
  // Get all favorite sellers for a user
  getByUserId: async (userId, params = {}) => {
    const response = await api.get(`/favorites/user/${userId}`, { params });
    return response.data;
  },

  // Check if a seller is favorited
  checkFavorite: async (userId, sellerId) => {
    const response = await api.get(`/favorites/check/${userId}/${sellerId}`);
    return response.data;
  },

  // Add seller to favorites
  add: async (userId, sellerId) => {
    const response = await api.post('/favorites', { userId, sellerId });
    return response.data;
  },

  // Remove seller from favorites
  remove: async (userId, sellerId) => {
    const response = await api.delete(`/favorites/${userId}/${sellerId}`);
    return response.data;
  },

  // Toggle favorite status
  toggle: async (userId, sellerId) => {
    const response = await api.post('/favorites/toggle', { userId, sellerId });
    return response.data;
  },

  // Get all favorite seller IDs for quick checks
  getIds: async (userId) => {
    const response = await api.get(`/favorites/ids/${userId}`);
    return response.data;
  }
};

export default api;
